<template>
  <div
    class="flex items-center cursor-pointer flex-shrink-0 whitespace-nowrap"
    :class="{
      'bg-white !border !border-solid !border-border-primary rounded-lg px-3 py-1.5 shadow-sm':
        !disableTooltip
    }"
    @click="navigateToAccount"
  >
    <span v-if="!disableTooltip" class="text-secondary-text mr-2">{{ t('Shift') }}:</span>

    <VTooltip
      location="bottom"
      open-delay="500"
      :disabled="disableTooltip || isNoShift || !tooltipText"
      :text="tooltipText"
    >
      <template #activator="{ props }">
        <VChip
          v-bind="props"
          variant="flat"
          class="font-semibold !border !border-solid"
          :style="{ '--v-border-opacity': '1' }"
          :class="
            isNoShift
              ? 'bg-gray-50 !border-gray-200 text-gray-700'
              : 'bg-success-50 !border-success-200 text-success-700'
          "
        >
          <span class="flex items-center">
            <span
              class="h-2 w-2 rounded-full mr-2"
              :class="isNoShift ? 'bg-gray-500' : 'bg-success-500'"
            ></span>
            {{ shiftText }}
          </span>
        </VChip>
      </template>
    </VTooltip>
  </div>
</template>

<script setup lang="ts">
import { computed, onMounted } from 'vue'
import { useI18n } from 'vue-i18n'
import { useRouter } from 'vue-router'
import { storeToRefs } from 'pinia'
import { useUserStore } from '@/stores/user'
import { UserSettingsService } from '@/services/telehealthApi/UserSettingsService'
import { ShiftTypeDTO } from '@/generated'
import { routeNames } from '@/router/routeNames'
import { isToday } from 'date-fns'
import { translateShiftTypeDTO } from '@/util/translations'
import { useFormatDate } from '@/composables/useFormatDate'
import { useShift } from '@/composables/useShift'

defineProps<{
  disableTooltip?: boolean
}>()

const { t } = useI18n()
const router = useRouter()
const userStore = useUserStore()
const { user, endOfShiftDate } = storeToRefs(userStore)
const { formatTime, formatDateTimeShort } = useFormatDate()
const { isShiftEnded } = useShift()

onMounted(() => {
  UserSettingsService.syncUserWithBackend()
})

const shiftType = computed(() => {
  if (isShiftEnded.value) return ShiftTypeDTO.NO_SHIFT
  return user.value?.currentShift || ShiftTypeDTO.NO_SHIFT
})
const isNoShift = computed(() => shiftType.value === ShiftTypeDTO.NO_SHIFT)

const shiftText = computed(() => {
  return translateShiftTypeDTO(shiftType.value, t, true)
})

const tooltipText = computed(() => {
  if (!endOfShiftDate.value) return undefined

  const timeStr = isToday(endOfShiftDate.value)
    ? formatTime(endOfShiftDate.value)
    : formatDateTimeShort(endOfShiftDate.value)

  return t('shift_ends_at', { time: timeStr })
})

function navigateToAccount() {
  router.push({ name: routeNames.USER_PROFILE })
}
</script>
